import { getPortalUrl } from "../lib/portal-url";

const API_PREFIX = "/_portal";

/** Tool-session cookie lives on the portal origin; management calls go there directly. */
function managementApiUrl(path: string): string {
  return `${getPortalUrl()}/api${path}`;
}

export interface ShareLinkItem {
  id: string;
  sid: string;
  url: string;
  code: string | null;
  config: Record<string, unknown> | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
  result: {
    completed: boolean;
    resultType: string | null;
    data: Record<string, unknown> | null;
    startedAt: string;
    completedAt: string;
  } | null;
}

async function portalFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(managementApiUrl(path), {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function listShareLinks(toolSlug: string): Promise<ShareLinkItem[]> {
  const data = await portalFetch<{ links: ShareLinkItem[] }>(
    `/tools/share?toolSlug=${encodeURIComponent(toolSlug)}`,
  );
  return data.links;
}

export async function createShareLink(input: {
  toolSlug: string;
  config?: Record<string, unknown>;
  code?: string;
  weeks?: number;
}): Promise<{ id: string; sid: string; url: string; expiresAt: string }> {
  return portalFetch("/tools/share", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deleteShareLink(id: string): Promise<void> {
  await portalFetch(`/tools/share/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function getShareResult(id: string): Promise<{
  completed: boolean;
  resultType: string | null;
  data: Record<string, unknown> | null;
  startedAt: string;
  completedAt: string;
}> {
  return portalFetch(`/tools/share/${encodeURIComponent(id)}/result`);
}

export async function deleteShareResult(id: string): Promise<void> {
  await portalFetch(`/tools/share/${encodeURIComponent(id)}/result`, {
    method: "DELETE",
  });
}

export interface ShareResultPayload {
  completed: boolean;
  resultType?: string;
  data?: Record<string, unknown>;
  startedAt: number;
}

export async function submitShareResult(
  token: string,
  payload: ShareResultPayload,
): Promise<void> {
  const res = await fetch(
    `${API_PREFIX}/share/${encodeURIComponent(token)}/result`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        startedAt: payload.startedAt,
      }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }
}

export async function submitShareResultWithRetry(
  token: string,
  payload: ShareResultPayload,
  maxAttempts = 5,
  delayMs = 2000,
): Promise<void> {
  let lastError: Error | null = null;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      await submitShareResult(token, payload);
      return;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (i < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
  }
  throw lastError ?? new Error("Verzenden mislukt");
}
